import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, AppSettingsService, CustomWindow, ShopperService, WINDOW } from '@woolworthsnz/styleguide';
import {
	AddOrUpdateTrolleyRequestV2,
	AddOrUpdateTrolleyResponseV2,
	RecipeCategoryResponse,
	RecipeListRequest,
	RecipeResponse,
	RecipeSearchRequest,
	RecipeSearchResponse,
	RecipeStamp,
	SavedRecipesResponse,
	TrolleyItemRequest,
} from '@woolworthsnz/trader-api';
import { EMPTY, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { RecipeSearchResponseMapped } from './recipe.model';
import { mapToRecipeStampLocal } from '../helpers';
import { RecipeStampLocal } from '../models';

@Injectable({
	providedIn: 'root',
})
export class RecipeService {
	private recipeDetailCache = new Map<number, RecipeResponse>();

	constructor(
		private apiService: ApiService,
		private appSettingsService: AppSettingsService,
		private shopperService: ShopperService,
		@Inject(WINDOW) private window: CustomWindow
	) {}

	getRecipeDetail(recipeId: number): Observable<RecipeResponse> {
		const cachedResult = this.recipeDetailCache.get(recipeId);

		return cachedResult
			? of(cachedResult)
			: this.apiService
					.get(`${this.appSettingsService.getEndpoint('recipes', 2)}/${recipeId}`)
					.pipe(tap((result) => this.recipeDetailCache.set(recipeId, result)));
	}

	isRecipeSaved(recipeId: number): Observable<HttpResponse<void>> {
		if (!(this.shopperService.state.isLoggedIn || this.window.BOOTSTRAP_DATA?.isShopperLoggedIn)) {
			return EMPTY;
		}
		return this.apiService.getWithFullResponse(`${this.appSettingsService.getEndpoint('savedRecipes')}/${recipeId}`);
	}

	getSavedRecipes(page?: number): Observable<SavedRecipesResponse> {
		if (!(this.shopperService.state.isLoggedIn || this.window.BOOTSTRAP_DATA?.isShopperLoggedIn)) {
			return EMPTY;
		}
		return this.apiService.get(`${this.appSettingsService.getEndpoint('savedRecipes')}`, { params: { page } });
	}

	saveRecipe(recipeId: number): Observable<HttpResponse<void>> {
		return this.apiService.putWithFullResponse(
			`${this.appSettingsService.getEndpoint('savedRecipes')}/${recipeId}`,
			{}
		);
	}

	removeSavedRecipe(recipeId: number): Observable<HttpResponse<void>> {
		return this.apiService.deleteWithFullResponse(
			`${this.appSettingsService.getEndpoint('savedRecipes')}/${recipeId}`,
			{}
		);
	}

	getRecipeSummary(recipeId: number): Observable<RecipeStampLocal> {
		return this.apiService
			.get(`${this.appSettingsService.getEndpoint('recipes')}/${recipeId}/summary`)
			.pipe(map(mapToRecipeStampLocal));
	}

	public getRecipeSummaryList(recipeIdList: number[]): Observable<RecipeStampLocal[]> {
		if (!recipeIdList.length) {
			return of([]);
		}

		const recipes = String(recipeIdList);
		return this.apiService
			.get(this.appSettingsService.getEndpoint('recipeSummary'), {
				params: { recipes },
			})
			.pipe(map((recipeList: RecipeStamp[]) => recipeList.map(mapToRecipeStampLocal)));
	}

	getRelatedRecipes(recipeId: number, count: number): Observable<RecipeStampLocal[]> {
		return this.apiService
			.get(`${this.appSettingsService.getEndpoint('recipes')}/${recipeId}/related`, {
				params: { size: count },
			})
			.pipe(map((stamps) => stamps.map((stamp: RecipeStamp) => mapToRecipeStampLocal(stamp))));
	}

	getAllCategories(): Observable<RecipeCategoryResponse> {
		return this.apiService.get(`${this.appSettingsService.getEndpoint('recipes')}/categories`);
	}

	getCategoryById(categoryId: number): Observable<RecipeCategoryResponse> {
		return this.apiService.get(`${this.appSettingsService.getEndpoint('recipes')}/categories/${categoryId}`);
	}

	getRecipesForCategory(categoryId: number, request: RecipeListRequest): Observable<RecipeSearchResponseMapped> {
		return this.apiService
			.get(`${this.appSettingsService.getEndpoint('recipes')}/categories/${categoryId}/recipes`, {
				params: { ...request },
			})
			.pipe(
				map((response: RecipeSearchResponse) => ({
					...response,
					recipes: response.recipes ? response.recipes.map(mapToRecipeStampLocal) : [],
				}))
			);
	}

	searchRecipes(request: RecipeSearchRequest): Observable<RecipeSearchResponseMapped> {
		return this.apiService
			.get(`${this.appSettingsService.getEndpoint('recipes')}/search`, { params: { ...request } })
			.pipe(
				map((response: RecipeSearchResponse) => ({
					...response,
					recipes: response.recipes ? response.recipes.map(mapToRecipeStampLocal) : [],
				}))
			);
	}

	addIngredientsToTrolley(items: TrolleyItemRequest[]): Observable<AddOrUpdateTrolleyResponseV2> {
		const body: AddOrUpdateTrolleyRequestV2 = {
			addToBasketSource: 'Recipe',
			items: items,
		};

		return this.apiService.post(this.appSettingsService.getEndpoint('addOrUpdateTrolley', 2), body);
	}
}
